import { reactive } from 'vue'
// @ts-ignore
import { EmojiIndex } from 'emoji-mart-vue-fast/src'
import data from 'emoji-mart-vue-fast/data/all.json'

interface WorkspaceState {
  emojiShortcode: string
  emojiSymbol: string
  loading: boolean
  error: string | null
  lastFetched: number | null
}

// Cache duration in milliseconds (10 minutes)
const CACHE_DURATION = 10 * 60 * 1000

// Create emoji index for converting shortcodes to symbols
const emojiIndex = new EmojiIndex(data)

const state = reactive<WorkspaceState>({
  emojiShortcode: ':pray:',
  emojiSymbol: '🙏',
  loading: false, // Start with loading false so we can trigger the initial fetch
  error: null,
  lastFetched: null
})

export function useWorkspaceStore() {
  const isStale = () => {
    if (!state.lastFetched) return true
    return Date.now() - state.lastFetched > CACHE_DURATION
  }

  const getEmojiSymbol = (shortcode: string) => {
    const cleanShortcode = shortcode.replace(/:/g, '')
    const emoji = emojiIndex.findEmoji(cleanShortcode)
    return emoji ? emoji.native : '🙏'
  }

  const fetchWorkspaceEmoji = async (force = false) => {
    console.log('fetchWorkspaceEmoji called with force:', force)
    console.log('Current state - loading:', state.loading, 'lastFetched:', state.lastFetched)
    
    // Don't fetch if we have recent data and not forcing refresh
    if (!force && !isStale() && !state.loading) {
      console.log('Using cached emoji data')
      return {
        emojiShortcode: state.emojiShortcode,
        emojiSymbol: state.emojiSymbol
      }
    }

    // Don't fetch if already loading
    if (state.loading) {
      console.log('Already loading, waiting for completion...')
      // Wait for the current fetch to complete
      while (state.loading) {
        await new Promise(resolve => setTimeout(resolve, 50))
      }
      console.log('Previous fetch completed')
      return {
        emojiShortcode: state.emojiShortcode,
        emojiSymbol: state.emojiSymbol
      }
    }

    console.log('Starting API call to fetch workspace emoji...')
    state.loading = true
    state.error = null

    try {
      const url = `${process.env.VUE_APP_API_BASE_URL}/workspace/settings`
      console.log('Making API call to:', url)
      
      const response = await fetch(url, {
        credentials: 'include'
      })

      console.log('API response status:', response.status, response.statusText)

      if (!response.ok) {
        throw new Error(`Failed to fetch workspace settings: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      console.log('API response data:', data)
      
      const emojiShortcode = data.emoji_shortcode || ':pray:'
      const emojiSymbol = getEmojiSymbol(emojiShortcode)
      
      console.log('Processed emoji - shortcode:', emojiShortcode, 'symbol:', emojiSymbol)

      state.emojiShortcode = emojiShortcode
      state.emojiSymbol = emojiSymbol
      state.lastFetched = Date.now()

      console.log('Workspace emoji fetch completed successfully')
      return {
        emojiShortcode: state.emojiShortcode,
        emojiSymbol: state.emojiSymbol
      }
    } catch (error) {
      console.error('Error in fetchWorkspaceEmoji:', error)
      state.error = error instanceof Error ? error.message : 'Failed to fetch workspace emoji'
      
      // Return default values on error
      return {
        emojiShortcode: ':pray:',
        emojiSymbol: '🙏'
      }
    } finally {
      state.loading = false
      console.log('Loading state set to false')
    }
  }

  const refreshWorkspaceEmoji = () => {
    return fetchWorkspaceEmoji(true)
  }

  const getWorkspaceEmoji = () => {
    // If we haven't fetched yet and we're not currently loading, trigger the initial fetch
    if (state.lastFetched === null && !state.loading) {
      console.log('Triggering initial workspace emoji fetch')
      fetchWorkspaceEmoji()
    }
    
    return {
      emojiShortcode: state.emojiShortcode,
      emojiSymbol: state.emojiSymbol,
      loading: state.loading,
      error: state.error
    }
  }

  return {
    fetchWorkspaceEmoji,
    refreshWorkspaceEmoji,
    getWorkspaceEmoji,
    getEmojiSymbol
  }
} 